import React, { useState, useMemo, useRef } from "react";
import {
  Button,
  Fade,
  Box,
  Typography,
  ButtonGroup,
  Divider,
} from "@mui/material";
import { Document, Page } from "react-pdf";
import api from "../../../api/axios";

const LoadPdf = ({ bookId, bookName, alternativeRute = "" }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  //  Estados
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1); //
  const [scale, setScale] = useState(1);
  const [pdfFlag, setPdfFlag] = useState(false);

  //  Generar URL del PDF (con timestamp para evitar caché)
  const generatePdfUrl = (fileName) => {
    if (!fileName) return null;
    return `${
      process.env.REACT_APP_API_URL
    }/library/book?pdf=${fileName}&bookId=${bookId}&v=${Date.now()}`;
  };

  const generatePdfUrlAnotherRute = (rute) => {
    const base = process.env.REACT_APP_API_URL.replace(/\/$/, "");
    const path = rute.replace(/^\//, "");

    return `${base}/${path}`;
  };

  const [pdfUrl] = useState(() =>
    alternativeRute
      ? generatePdfUrlAnotherRute(alternativeRute)
      : generatePdfUrl(bookName),
  );

  const pdfFile = useMemo(() => {
    if (!pdfUrl) return null;
    return { url: pdfUrl };
  }, [pdfUrl]);

  //  Zoom
  const zoomIn = () => setScale((prev) => prev + 0.1);
  const zoomOut = () => setScale((prev) => (prev > 0.6 ? prev - 0.1 : prev));

  //  Cuando se carga el PDF correctamente
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);

    // Desactivar después de 3 segundos
    setTimeout(() => {
      setPdfFlag(true);
    }, 2000);
  };

  //  Descargar PDF
  const handleDownloadPdf = () => {
    const url = alternativeRute
      ? generatePdfUrlAnotherRute(alternativeRute)
      : generatePdfUrl(bookName);

    const link = document.createElement("a");
    link.href = url;
    link.download = bookName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //  Obtener archivo desde API
  const getPdfFile = async (fileName) => {
    const url = alternativeRute
      ? generatePdfUrlAnotherRute(alternativeRute)
      : generatePdfUrl(fileName);

    const response = await api.get(url, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });

    return blob;
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setStartY(e.pageY - containerRef.current.offsetTop);
    setScrollLeft(containerRef.current.scrollLeft);
    setScrollTop(containerRef.current.scrollTop);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    e.preventDefault();

    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;

    const walkX = x - startX;
    const walkY = y - startY;

    containerRef.current.scrollLeft = scrollLeft - walkX;
    containerRef.current.scrollTop = scrollTop - walkY;
  };

  return (
    <>
      {pdfUrl ? (
        <>
          {pdfFlag && numPages && pageNumber && (
            <Fade in timeout={2000}>
              <div className="pdf-viewer" style={{ width: "100%" }}>
                <Box sx={{ width: "100%", p: 2 }}>
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontWeight: 600, mb: 1 }}>
                    Documento
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                    }}>
                    {/* PAGINACIÓN */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">
                        Página {pageNumber} de {numPages || "?"}
                      </Typography>

                      <ButtonGroup size="small" variant="outlined">
                        <Button
                          onClick={() => setPageNumber((p) => p - 1)}
                          disabled={pageNumber <= 1}>
                          Anterior
                        </Button>

                        <Button
                          onClick={() => setPageNumber((p) => p + 1)}
                          disabled={pageNumber >= numPages}>
                          Siguiente
                        </Button>
                      </ButtonGroup>
                    </Box>

                    {/* ZOOM */}
                    <ButtonGroup size="small" variant="outlined">
                      <Button onClick={zoomOut}>-</Button>
                      <Button
                        disabled
                        sx={{
                          fontWeight: 700,
                          "&.Mui-disabled": {
                            color: "#1976d2",
                            backgroundColor: "rgba(25,118,210,0.08)",
                            opacity: 1,
                          },
                        }}>
                        Zoom
                      </Button>
                      <Button onClick={zoomIn}>+</Button>
                    </ButtonGroup>

                    {/* DESCARGA */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleDownloadPdf(bookName)}>
                      Ver/Descargar PDF
                    </Button>
                  </Box>
                </Box>
              </div>
            </Fade>
          )}
          <Document
            file={pdfFile}
            onLoadSuccess={onDocumentLoadSuccess}
            noData="No se encontró el PDF."
            loading="Cargando PDF...">
            {pdfFlag && (
              <Box
                ref={containerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                sx={{
                  width: "100%",
                  height: "100%",
                  overflow: "auto",
                  cursor: isDragging ? "grabbing" : "grab",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  userSelect: "none",
                }}>
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            )}
          </Document>
        </>
      ) : (
        <div>No hay archivo PDF disponible.</div>
      )}
    </>
  );
};

export default LoadPdf;
